export default function sketch(p, options = {}) {
  const invert = options.invert ?? false;

  //////////////////
  // PARAMETERS
  //////////////////
  const pointsInCharacter = 6;
  const circleRadius = 100;
  const ellipseSqueezeFactor = 1;
  const ellipseRotationAngle = Math.PI / 4;
  const characterWidth = 20;
  const characterGap = 30;
  const spreadMagnitude = 60;
  const numberOfLines = 60;
  const lineWidth = 20;
  const maxTransparency = 12;

  const rows = 2; // number of rows
  const verticalGap = 300; // vertical spacing between rows

  // arrays for points
  const rowPointsArray = [];
  const pointOffsets = [];
  const prevRowPointsArray = [];
  const shadowOffsets = [];

  const speed = 0.01;
  const moveMagnitude = 50;

  let grainGraphics = null;

  // internal frame counter for pre-warm
  let internalFrame = 1;

  //////////////////
  // SETUP
  //////////////////
  p.setup = () => {
    p.frameRate(20);
    p.createCanvas(window.innerWidth, window.innerHeight);
    p.noStroke();

    const centre = [characterWidth / 8, characterWidth];

    // generate points for each row
    for (let row = 0; row < rows; row++) {
      let maxX = 0;
      const rowPoints = [];
      const rowOffsets = [];
      const rowShadowOffsets = [];

      for (let i = 0; i < 20; i++) {
        const points = getCharacter(centre);

        for (let j = 0; j < points.length; j++) {
          const [x, y] = points[j];
          rowPoints.push([x + maxX, y + 130 + row * verticalGap]);
          rowOffsets.push([p.random(1000), p.random(1000)]);
          rowShadowOffsets.push([p.random(1000), p.random(1000)]);
        }

        maxX += getMaxXOfCharacter(points) + characterGap;
      }

      rowPointsArray.push(rowPoints);
      pointOffsets.push(rowOffsets);
      shadowOffsets.push(rowShadowOffsets);
      // initialize with zeros, will pre-warm next
      prevRowPointsArray.push(rowPoints.map(([x, y]) => [x, y]));
    }

    // pre-warm: simulate frame 1 for smooth start
    const topScreenOffset = 0.12;
    const verticalOffset = p.height * topScreenOffset;

    rowPointsArray.forEach((rowPoints, r) => {
      const offsets = pointOffsets[r];

      const movingPoints = rowPoints.map(([x, y], i) => {
        const nx = p.noise(offsets[i][0] + internalFrame * speed);
        const ny = p.noise(offsets[i][1] + internalFrame * speed);
        const dx = p.map(nx, 0, 1, -moveMagnitude, moveMagnitude);
        const dy = p.map(ny, 0, 1, -moveMagnitude, moveMagnitude);
        return [x + dx, y + verticalOffset + dy];
      });

      prevRowPointsArray[r] = movingPoints.map(([x, y]) => [x, y]);
    });

    // Create full-screen grain texture
    makeGrain(p);
  };

  //////////////////
  // DRAW
  //////////////////
  p.draw = () => {
    let backgroundColour = [255, 255, 255];
    let colour = [146, 145, 145];

    if (invert) {
      colour = backgroundColour;
      backgroundColour = [69, 68, 68];
    }

    p.background(...backgroundColour);
    p.strokeWeight(lineWidth);
    p.noFill();

    const topScreenOffset = 0.12;
    const verticalOffset = p.height * topScreenOffset;

    rowPointsArray.forEach((rowPoints, r) => {
      const offsets = pointOffsets[r];
      const shadows = shadowOffsets[r];
      const prevPoints = prevRowPointsArray[r];

      // compute moving points using internalFrame
      const movingPoints = rowPoints.map(([x, y], i) => {
        const nx = p.noise(offsets[i][0] + internalFrame * speed);
        const ny = p.noise(offsets[i][1] + internalFrame * speed);
        const dx = p.map(nx, 0, 1, -moveMagnitude, moveMagnitude);
        const dy = p.map(ny, 0, 1, -moveMagnitude, moveMagnitude);
        return [x + dx, y + verticalOffset + dy];
      });

      // compute shadow points
      const shadowPoints = movingPoints.map(([x, y], i) => {
        const nx = p.noise(shadows[i][0] + internalFrame * speed);
        const ny = p.noise(shadows[i][1] + internalFrame * speed);
        const dx = p.map(nx, 0, 1, -spreadMagnitude, spreadMagnitude);
        const dy = p.map(ny, 0, 1, -spreadMagnitude, spreadMagnitude);
        return [x + dx, y + dy];
      });

      // draw multiple lines with interpolation
      for (let i = 0; i < numberOfLines; i++) {
        const t = i / numberOfLines;
        p.beginShape();

        // first duplicate point for spline
        const first = movingPoints[0];
        p.splineVertex(first[0], first[1]);
        p.splineVertex(first[0], first[1]);

        movingPoints.forEach((_, j) => {
          const [pxPrev, pyPrev] = prevPoints[j];
          const bx = p.lerp(pxPrev, shadowPoints[j][0], t);
          const by = p.lerp(pyPrev, shadowPoints[j][1], t);
          const alpha = (1 - (i + 1) / numberOfLines) * maxTransparency;
          p.stroke(...colour, alpha);
          p.splineVertex(bx, by);
        });

        // last duplicate point for spline
        const last = movingPoints[movingPoints.length - 1];
        p.splineVertex(last[0], last[1]);
        p.splineVertex(last[0], last[1]);

        p.endShape();
      }

      // update previous points for next frame
      prevRowPointsArray[r] = movingPoints.map(([x, y]) => [x, y]);
    });

    // --- Add grain overlay ---
    if (grainGraphics) {
      p.push();
      p.tint(255, 80);
      p.image(grainGraphics, 0, 0, p.width, p.height);
      p.pop();
    }

    internalFrame++; // advance internal frame counter
  };

  //////////////////
  // HELPERS
  //////////////////
  function makeGrain(p) {
    const d = p.pixelDensity();
    const w = window.innerWidth;
    const h = window.innerHeight;

    // Create the offscreen buffer at display size
    grainGraphics = p.createGraphics(w, h);
    grainGraphics.pixelDensity(d);
    grainGraphics.loadPixels();

    const total = w * h * d * d * 4;

    for (let i = 0; i < total; i += 4) {
      const v = p.random(255);
      grainGraphics.pixels[i] = v;
      grainGraphics.pixels[i + 1] = v;
      grainGraphics.pixels[i + 2] = v;
      grainGraphics.pixels[i + 3] = 150;
    }

    grainGraphics.updatePixels();
  }

  function getCharacter(centre) {
    const points = [];
    for (let i = 0; i < pointsInCharacter; i++) {
      const [x, y] = getRandomPointInEllipse(centre);
      points.push([x, y]);
    }
    return points;
  }

  function getMaxXOfCharacter(points) {
    return Math.max(...points.map(([x]) => x));
  }

  function getRandomPointInCircle(centre) {
    const r = Math.sqrt(Math.random()) * circleRadius;
    const angle = Math.random() * 2 * Math.PI;
    const x = centre[0] + r * Math.cos(angle);
    const y = centre[1] + r * Math.sin(angle);
    return [x, y];
  }

  function getRandomPointInEllipse(centre) {
    let [x, y] = getRandomPointInCircle(centre);
    x = centre[0] + (x - centre[0]) * ellipseSqueezeFactor;

    const dx = x - centre[0];
    const dy = y - centre[1];
    const cosAngle = Math.cos(ellipseRotationAngle);
    const sinAngle = Math.sin(ellipseRotationAngle);

    const rotatedX = dx * cosAngle - dy * sinAngle + centre[0];
    const rotatedY = dx * sinAngle + dy * cosAngle + centre[1];

    return [rotatedX, rotatedY];
  }
}